import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { z } from "zod";
import {
  isMaterialStep,
  type MaterialKind,
  MaterialKindSchema,
} from "../../../skills/leadcue-reply/scripts/lib/materials.ts";
import type { PlanStep } from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { AttachmentIcon, CheckIcon } from "./icons.tsx";
import { usePlanApi, useWorkspace, workspaceApi } from "./workspace-context.tsx";

/*
 * A lead asked for a material the campaign has nothing saved for (`docs/2026-10-02-materials-and-
 * references.md`, "The card"). The app wrote no draft; the rep says once what replies do about it,
 * and the answer is kept for the campaign. Built in the lead-link and outside-goal sheets' style: one
 * card, one gold action, quiet ways out. The choice form is shared with the campaign page's Materials
 * row (`campaign-materials.tsx`), so a rep sets a kind the same way wherever they meet it.
 */

/** The four ways out, as the routes name them. `to_call_reply` exists on the card only. */
const AnswerSchema = z.enum([
  "present_file",
  "present_later",
  "present_link",
  "existing_resource",
  "to_call_campaign",
  "to_call_reply",
  "confirmed_absent",
]);
type Answer = z.infer<typeof AnswerSchema>;

export type MaterialChoice =
  | { choice: "present_file"; fileId: string }
  | { choice: "present_later" }
  | { choice: "present_link"; url: string; label: string }
  | { choice: "existing_resource"; resourceIndex: number }
  | { choice: "to_call_campaign" }
  | { choice: "to_call_reply" }
  | { choice: "confirmed_absent" };

export const MaterialFileSchema = z.object({
  fileName: z.string(),
  bytes: z.number().int().nonnegative(),
  mediaType: z.string(),
  sha256: z.string(),
});

export const ResourceSchema = z.object({
  index: z.number().int().nonnegative(),
  url: z.string(),
  label: z.string(),
});
export type Resource = z.infer<typeof ResourceSchema>;

const RequestSchema = z.object({
  campaignId: z.string(),
  providerReplyId: z.number(),
  runId: z.string(),
  kinds: z.array(
    z.object({
      kind: MaterialKindSchema,
      label: z.string(),
      state: z.enum(["open", "answered"]),
      answer: AnswerSchema.nullable(),
    }),
  ),
  waiting: z.number().int().nonnegative(),
  hasCall: z.boolean(),
  resources: z.array(ResourceSchema),
});
type MaterialRequestView = z.infer<typeof RequestSchema>;
const RequestViewSchema = z.object({ request: RequestSchema.nullable() });
const AnswerResultSchema = z.object({
  request: RequestSchema.nullable(),
  redraft: z
    .object({
      redrafted: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
      left: z.number().int().nonnegative(),
    })
    .nullable(),
});

const UploadedSchema = z.object({
  file: MaterialFileSchema.extend({ fileId: z.string() }),
});
export type UploadedFile = z.infer<typeof UploadedSchema>["file"];

const RefusalSchema = z.object({ error: z.string() });

/** The route's refusal code, or null when the body is not one. */
export async function refusalOf(response: Response): Promise<string | null> {
  const body = RefusalSchema.safeParse(await response.json().catch(() => null));
  return body.success ? body.data.error : null;
}

/** The open card on a plan, or null. Only a pending step is open, as with the lead-link task. */
export function openMaterialRequestStep(plan: { steps: readonly PlanStep[] }): PlanStep | null {
  return plan.steps.find((step) => isMaterialStep(step) && step.state === "pending") ?? null;
}

/* Files ---------------------------------------------------------------------------------------- */

export const MATERIAL_FILE_ACCEPT = ".pdf,.pptx,.docx,.png,.jpg,.jpeg";
/** 5 MB, the step's own limit: base64 adds about a third and mailbox limits count the encoding. */
const MATERIAL_FILE_LIMIT = 5_242_880;

const MEDIA_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
};

/** A size as a rep reads it: "340 KB", "1.2 MB". */
export function fileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FILE_TYPE_REFUSAL =
  "That file can't be attached. Use a PDF, PowerPoint (.pptx), Word (.docx), PNG or JPEG.";
const FILE_SIZE_REFUSAL = "That file is over 5 MB. Share a link to it instead.";
const FILE_EMPTY_REFUSAL = "That file is empty. Choose the file again.";

/** Why the upload route refused a file, in words the rep can act on, or null for another code. */
function uploadRefusal(code: string | null): string | null {
  switch (code) {
    case "material_file_type":
      return FILE_TYPE_REFUSAL;
    case "material_file_too_large":
      return FILE_SIZE_REFUSAL;
    case "material_file_empty":
      return FILE_EMPTY_REFUSAL;
    case "material_keys_unconfigured":
      return "Files can't be stored here yet. Share a link instead.";
    default:
      return null;
  }
}

/** Checked before any byte leaves the browser, with the same words the route would answer. */
function localFileProblem(file: File): string | null {
  const extension = file.name.split(".").at(-1)?.toLowerCase() ?? "";
  if (!(extension in MEDIA_TYPES)) return FILE_TYPE_REFUSAL;
  if (file.size === 0) return FILE_EMPTY_REFUSAL;
  if (file.size > MATERIAL_FILE_LIMIT) return FILE_SIZE_REFUSAL;
  return null;
}

/** Route 1: the file's raw bytes into the campaign's store. No material row exists until a save. */
export async function uploadMaterialFile(
  workspaceId: string,
  campaignId: string,
  file: File,
): Promise<UploadedFile> {
  const problem = localFileProblem(file);
  if (problem !== null) throw new Error(problem);
  const extension = file.name.split(".").at(-1)?.toLowerCase() ?? "";
  const response = await fetch(
    `${workspaceApi(workspaceId)}/campaigns/${encodeURIComponent(campaignId)}/materials/files`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "content-type": file.type || MEDIA_TYPES[extension] || "application/octet-stream",
        "x-leadcue-reply-file-name": encodeURIComponent(file.name),
      },
      body: file,
    },
  );
  if (response.status === 413) throw new Error(FILE_SIZE_REFUSAL);
  if (!response.ok)
    throw new Error(
      uploadRefusal(await refusalOf(response)) ??
        `Could not upload ${file.name}. Nothing was saved.`,
    );
  return UploadedSchema.parse(await response.json()).file;
}

/** A pasted link the routes accept: https only. */
function linkProblem(value: string): string | null {
  const text = value.trim();
  if (text === "") return null;
  try {
    const url = new URL(text);
    if (url.protocol !== "https:" || !url.hostname.includes("."))
      return "Paste a link that starts with https://";
    return null;
  } catch {
    return "Paste a link that starts with https://";
  }
}

export const displayLink = (url: string): string =>
  url.replace(/^https:\/\//u, "").replace(/\/$/u, "");

/** "Deck", "Case study": a kind's label at the start of a line. */
export const capitalised = (label: string): string =>
  label.length === 0 ? label : `${label[0]?.toUpperCase()}${label.slice(1)}`;

/* The choice form ------------------------------------------------------------------------------ */

type Option = "share" | "existing" | "call" | "none";

/**
 * The four ways out for one kind, in the design's order, as one radio group with one gold save. The
 * chosen option opens its own fields in place. "Point them to the call" asks which replies only on
 * the card: the campaign page sets the campaign. Pricing and proposal on the campaign page add the
 * "OK to send before a call" switch to a file or link.
 */
export function MaterialChoiceForm(props: {
  label: string;
  resources: readonly Resource[];
  hasCall: boolean;
  scope: "card" | "page";
  shareableSwitch: boolean;
  busy: boolean;
  submitLabel: string;
  upload: (file: File) => Promise<UploadedFile>;
  onSubmit: (choice: MaterialChoice, shareable: boolean | null) => Promise<void>;
  onCancel?: () => void;
}) {
  const id = useId();
  const [option, setOption] = useState<Option>("share");
  const [file, setFile] = useState<UploadedFile | null>(null);
  // "We have one, I'll upload it before sending": a way to share it, saved by the same gold button.
  const [later, setLater] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [link, setLink] = useState("");
  const [linkTouched, setLinkTouched] = useState(false);
  // Save stays live; pressing it with nothing chosen says what is missing instead of doing nothing.
  const [attempted, setAttempted] = useState(false);
  const [resource, setResource] = useState<number | null>(
    props.resources.length === 1 ? (props.resources[0]?.index ?? null) : null,
  );
  // The card saves for the campaign, as its first lines say; "Just this reply" is the deliberate exception.
  const [callScope, setCallScope] = useState<"reply" | "campaign">("campaign");
  const [shareable, setShareable] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const pickButton = useRef<HTMLButtonElement>(null);
  const removeButton = useRef<HTMLButtonElement>(null);
  // Focus follows the control that replaced the one pressed, never falling to the page body.
  const moveFocus = useRef<"remove" | "pick" | null>(null);
  useEffect(() => {
    const target = moveFocus.current;
    moveFocus.current = null;
    if (target === "remove") removeButton.current?.focus();
    if (target === "pick") pickButton.current?.focus();
  });
  const linkError = linkProblem(link);
  const busy = props.busy || uploading !== null;
  const ready =
    option === "share"
      ? file !== null || later || (link.trim() !== "" && linkError === null)
      : option === "existing"
        ? resource !== null
        : true;
  const choose = async (picked: File | undefined) => {
    if (!picked) return;
    setProblem(null);
    setUploading(picked.name);
    try {
      setFile(await props.upload(picked));
      setLater(false);
      moveFocus.current = "remove";
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "Could not upload that file.");
    } finally {
      setUploading(null);
      if (picker.current) picker.current.value = "";
    }
  };
  const submit = () => {
    const choice: MaterialChoice | null =
      option === "share"
        ? file !== null
          ? { choice: "present_file", fileId: file.fileId }
          : later
            ? { choice: "present_later" }
            : linkError === null && link.trim() !== ""
              ? { choice: "present_link", url: link.trim(), label: capitalised(props.label) }
              : null
        : option === "existing"
          ? resource === null
            ? null
            : { choice: "existing_resource", resourceIndex: resource }
          : option === "call"
            ? props.scope === "card" && callScope === "reply"
              ? { choice: "to_call_reply" }
              : { choice: "to_call_campaign" }
            : { choice: "confirmed_absent" };
    if (choice === null) {
      setAttempted(true);
      setLinkTouched(true);
      return;
    }
    const withSwitch =
      props.shareableSwitch &&
      (option === "existing" || (option === "share" && choice.choice !== "present_later"));
    void props.onSubmit(choice, withSwitch ? shareable : null);
  };
  const missing = attempted && !ready;
  const radio = (value: Option, title: string, hint: string | null, fields: ReactNode) => (
    <div className="lcr-material-option" data-selected={option === value || undefined}>
      <label className="lcr-material-choice">
        <input
          type="radio"
          name={`${id}-option`}
          value={value}
          checked={option === value}
          disabled={busy}
          onChange={() => {
            setOption(value);
            setAttempted(false);
          }}
        />
        <span className="lcr-material-choice-text">
          <span className="lcr-material-choice-title">{title}</span>
          {hint && <span className="lcr-material-choice-hint">{hint}</span>}
        </span>
      </label>
      {option === value && fields && <div className="lcr-material-fields">{fields}</div>}
    </div>
  );
  return (
    <form
      className="lcr-material-form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <fieldset className="lcr-material-options" disabled={props.busy}>
        <legend className="lcr-visually-hidden">What replies do when a lead asks for it</legend>
        {radio(
          "share",
          "Share it",
          "Upload a file (PDF, PowerPoint, Word or an image, up to 5 MB), or paste a link.",
          <>
            <input
              ref={picker}
              type="file"
              accept={MATERIAL_FILE_ACCEPT}
              className="lcr-visually-hidden"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(event) => void choose(event.target.files?.[0])}
            />
            {file || later ? (
              <p className="lcr-material-file" data-later={later || undefined}>
                {file ? (
                  <>
                    <AttachmentIcon size={16} className="lcr-material-file-icon" />
                    <span className="lcr-material-file-name">{file.fileName}</span>
                    <span className="lcr-material-file-size">{fileSize(file.bytes)}</span>
                  </>
                ) : (
                  <>
                    <CheckIcon size={16} className="lcr-material-file-icon" />
                    <span className="lcr-material-later-text">
                      We have one, I'll upload it before sending
                    </span>
                  </>
                )}
                <button
                  ref={removeButton}
                  type="button"
                  className="lcr-linklike"
                  disabled={busy}
                  onClick={() => {
                    setFile(null);
                    setLater(false);
                    moveFocus.current = "pick";
                  }}
                >
                  {file ? "Remove" : "Undo"}
                </button>
              </p>
            ) : (
              <>
                <div className="lcr-material-share">
                  <button
                    ref={pickButton}
                    type="button"
                    className="lcr-material-pick"
                    disabled={busy}
                    onClick={() => picker.current?.click()}
                  >
                    <AttachmentIcon size={16} />
                    {uploading ? `Uploading ${uploading}…` : "Choose a file"}
                  </button>
                  <span className="lcr-material-or" aria-hidden="true">
                    or
                  </span>
                  <label className="lcr-material-link">
                    <span className="lcr-visually-hidden">Or paste a link</span>
                    <input
                      type="url"
                      inputMode="url"
                      placeholder="Paste a link: https://…"
                      value={link}
                      disabled={busy}
                      aria-invalid={linkTouched && linkError !== null ? true : undefined}
                      onBlur={() => setLinkTouched(true)}
                      onChange={(event) => setLink(event.target.value)}
                    />
                  </label>
                </div>
                <button
                  type="button"
                  className="lcr-linklike lcr-material-later"
                  disabled={busy}
                  onClick={() => {
                    setLater(true);
                    setAttempted(false);
                    moveFocus.current = "remove";
                  }}
                >
                  We have one, I'll upload it before sending
                </button>
              </>
            )}
            {linkTouched && linkError !== null && !file && !later && (
              <p className="lcr-material-problem" role="alert">
                {linkError}
              </p>
            )}
            {missing && linkError === null && (
              <p className="lcr-material-problem" role="alert">
                Choose a file or paste a link first.
              </p>
            )}
            {problem && (
              <p className="lcr-material-problem" role="alert">
                {problem}
              </p>
            )}
          </>,
        )}
        {props.resources.length > 0 &&
          radio(
            "existing",
            "Send something we already have",
            null,
            <>
              <div className="lcr-material-resources" role="radiogroup" aria-label="Saved links">
                {props.resources.map((entry) => (
                  <label key={entry.index} className="lcr-material-subchoice">
                    <input
                      type="radio"
                      name={`${id}-resource`}
                      checked={resource === entry.index}
                      disabled={busy}
                      onChange={() => {
                        setResource(entry.index);
                        setAttempted(false);
                      }}
                    />
                    <span className="lcr-material-choice-text">
                      <span>{entry.label}</span>
                      <span className="lcr-material-choice-hint lcr-material-url">
                        {displayLink(entry.url)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {missing && (
                <p className="lcr-material-problem" role="alert">
                  Pick one of the saved links first.
                </p>
              )}
            </>,
          )}
        {props.hasCall &&
          radio(
            "call",
            "Point them to the call",
            null,
            props.scope === "card" ? (
              <div className="lcr-material-scope" role="radiogroup" aria-label="Which replies">
                <label className="lcr-material-segment">
                  <input
                    type="radio"
                    name={`${id}-scope`}
                    checked={callScope === "reply"}
                    disabled={busy}
                    onChange={() => setCallScope("reply")}
                  />
                  <span>Just this reply</span>
                </label>
                <label className="lcr-material-segment">
                  <input
                    type="radio"
                    name={`${id}-scope`}
                    checked={callScope === "campaign"}
                    disabled={busy}
                    onChange={() => setCallScope("campaign")}
                  />
                  <span>Every reply in this campaign</span>
                </label>
              </div>
            ) : null,
          )}
        {radio("none", "We don't have one", null, null)}
      </fieldset>
      {props.shareableSwitch && (option === "existing" || (option === "share" && !later)) && (
        <ShareableSwitch checked={shareable} disabled={busy} onChange={setShareable} />
      )}
      <div className="lcr-actions lcr-material-actions">
        <button type="submit" className="lcr-primary" disabled={busy}>
          {props.submitLabel}
        </button>
        {props.onCancel && (
          <button
            type="button"
            className="lcr-linklike lcr-material-cancel"
            disabled={props.busy}
            onClick={props.onCancel}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

/** Pricing and proposal stay behind the call until the rep says a saved one may go before it. */
export function ShareableSwitch(props: {
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="lcr-material-switch">
      <input
        type="checkbox"
        role="switch"
        aria-checked={props.checked}
        checked={props.checked}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.target.checked)}
      />
      <span>OK to send before a call</span>
    </label>
  );
}

/* The card ------------------------------------------------------------------------------------- */

/** What the rep chose for a kind already answered on this card, in their words. */
const ANSWERED: Record<Answer, string> = {
  present_file: "sharing a file",
  present_later: "you'll upload it before sending",
  present_link: "sharing a link",
  existing_resource: "sending a saved link",
  to_call_campaign: "pointed to the call, every reply",
  to_call_reply: "pointed to the call, this reply",
  confirmed_absent: "we don't have one",
};

/** "a deck", "pricing": what the lead asked for, as the heading names it. */
const asked = (kind: MaterialKind, label: string): string =>
  kind === "pricing" ? label : `a ${label}`;

function listed(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}

function MaterialRequestSheet(props: {
  request: MaterialRequestView | null;
  quote: string | null;
  busy: boolean;
  error: string | null;
  upload: (file: File) => Promise<UploadedFile>;
  onAnswer: (kind: MaterialKind, choice: MaterialChoice) => Promise<void>;
  onReply: () => void;
}) {
  const request = props.request;
  const kinds = request?.kinds ?? [];
  const open = kinds.filter((entry) => entry.state === "open");
  const several = kinds.length > 1;
  // One kind at a time, so the card has one gold action: the first still open; the rest wait.
  const current = open[0]?.kind ?? null;
  // After one kind is answered, focus moves to the next one still open, never to the page body.
  const headings = useRef<Partial<Record<MaterialKind, HTMLHeadingElement | null>>>({});
  const openCount = open.length;
  const previous = useRef(openCount);
  useEffect(() => {
    if (openCount < previous.current && current !== null) headings.current[current]?.focus();
    previous.current = openCount;
  }, [openCount, current]);
  return (
    <article className="lcr-sheet lcr-material" aria-labelledby="lcr-material-heading">
      <h3 id="lcr-material-heading">
        {request
          ? `They asked for ${listed(kinds.map((entry) => asked(entry.kind, entry.label)))}`
          : "They asked for material"}
      </h3>
      {props.quote && (
        <blockquote className="lcr-material-quote">
          <q dir="auto">{props.quote}</q>
        </blockquote>
      )}
      <p className="lcr-material-lede">
        Nothing is saved for this campaign yet. Your answer is saved for the campaign, so the next
        lead who asks gets a draft straight away.
      </p>
      {/* Read before choosing: how far the answer reaches is part of the choice. */}
      {request && request.waiting > 0 && (
        <p className="lcr-material-waiting">
          {request.waiting === 1
            ? "1 other conversation in this campaign is waiting on this answer."
            : `${request.waiting} other conversations in this campaign are waiting on this answer.`}
        </p>
      )}
      {request === null && <p className="lcr-meta-text">Loading…</p>}
      {request &&
        kinds.map((entry) => {
          const asking = entry.kind === current;
          return (
            <section
              key={entry.kind}
              className="lcr-material-kind"
              data-state={asking ? "asking" : entry.state}
              aria-labelledby={several ? `lcr-material-${entry.kind}` : undefined}
              aria-label={several ? undefined : `What replies do about the ${entry.label}`}
            >
              {several && (
                <h4
                  id={`lcr-material-${entry.kind}`}
                  className="lcr-material-kind-title"
                  tabIndex={-1}
                  ref={(node) => {
                    headings.current[entry.kind] = node;
                  }}
                >
                  {capitalised(entry.label)}
                  {entry.state === "answered" && entry.answer && (
                    <span className="lcr-material-answered">
                      <CheckIcon size={14} /> {ANSWERED[entry.answer]}
                    </span>
                  )}
                  {entry.state === "open" && !asking && (
                    <span className="lcr-material-next">Next</span>
                  )}
                </h4>
              )}
              {asking && (
                <MaterialChoiceForm
                  label={entry.label}
                  resources={request.resources}
                  hasCall={request.hasCall}
                  scope="card"
                  shareableSwitch={false}
                  busy={props.busy}
                  submitLabel={open.length === 1 ? "Save and draft" : "Save"}
                  upload={props.upload}
                  onSubmit={(choice) => props.onAnswer(entry.kind, choice)}
                />
              )}
            </section>
          );
        })}
      {several && open.length > 1 && (
        <p className="lcr-meta-text">The reply is drafted once each of these is answered.</p>
      )}
      {props.error && (
        <p className="lcr-material-problem" role="alert">
          {props.error}
        </p>
      )}
      <button type="button" className="lcr-linklike" disabled={props.busy} onClick={props.onReply}>
        Write a reply yourself <kbd className="lcr-kbd">R</kbd>
      </button>
    </article>
  );
}

/** Why an answer was refused, in words the rep can act on; the route names the code. */
export function materialAnswerRefusal(code: string | null): string {
  switch (code) {
    case "material_request_closed":
      return "This was already answered, or the lead wrote again.";
    case "material_file_unknown":
      return "That file was not found. Upload it again.";
    case "material_link_invalid":
      return "Paste a link that starts with https://";
    case "resource_unknown":
      return "That saved link is no longer there. Pick another.";
    case "no_call":
      return "This campaign has no call to point them to.";
    default:
      return `The answer could not be saved (${code ?? "error"}). Nothing changed.`;
  }
}

const materialRequestKey = (planId: string) => ["material-request", planId] as const;

/**
 * The card with its data: the request read from the server and each answer posted to it. Once every
 * kind is answered the server redrafts this conversation and its siblings; what it did is said to
 * the page (`onSaid`), because the card closes with the step.
 */
export function MaterialRequestCard(props: {
  planId: string;
  quote: string | null;
  onChange: () => Promise<void>;
  onSaid: (said: string) => void;
  onReply: () => void;
}) {
  const planApi = usePlanApi(props.planId);
  const workspace = useWorkspace();
  const cache = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = materialRequestKey(props.planId);
  const view = useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await fetch(`${planApi}/material-request`, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`material_request_${response.status}`);
      return RequestViewSchema.parse(await response.json()).request;
    },
  });
  const request = view.data ?? null;
  return (
    <MaterialRequestSheet
      request={request}
      quote={props.quote}
      busy={busy}
      error={view.isError ? "This request could not be read. Refresh to try again." : error}
      upload={(file) => {
        if (!request) return Promise.reject(new Error("The request is still loading."));
        return uploadMaterialFile(workspace.id, request.campaignId, file);
      }}
      onAnswer={async (kind, choice) => {
        setBusy(true);
        setError(null);
        try {
          const response = await fetch(`${planApi}/material-request`, {
            method: "POST",
            credentials: "same-origin",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ kind, ...choice }),
          });
          if (!response.ok) {
            const code = await refusalOf(response);
            // A request that closed under the rep is not theirs to fix: show where it stands now.
            if (code === "material_request_closed") {
              await cache.invalidateQueries({ queryKey: key });
              await props.onChange();
            }
            throw new Error(materialAnswerRefusal(code));
          }
          const result = AnswerResultSchema.parse(await response.json());
          cache.setQueryData(key, result.request);
          // Another kind on this turn is still open: the card stays and moves on to it.
          if (result.redraft === null && result.request !== null) return;
          if (result.redraft && result.redraft.total > 1) {
            const { redrafted, total, left } = result.redraft;
            props.onSaid(
              `Redrafted ${redrafted} of ${total} conversations waiting on this; ${left} left for you`,
            );
          }
          await cache.invalidateQueries({ queryKey: ["campaign-materials"] });
          await props.onChange();
        } catch (cause) {
          setError(cause instanceof Error ? cause.message : "The answer could not be saved.");
        } finally {
          setBusy(false);
        }
      }}
      onReply={props.onReply}
    />
  );
}
